<template>
  <div>
    <Masthead title="Compare NoSQLBooster with Studio 3T" />
    <section>
      <div class="container">
        <CompareFeaturesTable :headers="headers" :feature-categories="featureCategories" />
        <MarkdownContent :source="mdSource" />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import Masthead from '@/components/Masthead.vue';
import MarkdownContent from '@/components/MarkdownContent.vue';
import CompareFeaturesTable from '@/components/CompareFeaturesTable.vue';
import mdSource from '@/content/compareWithStudio3T.md';

interface FeatureCategory {
  name: string;
  uri?: string;
  rows: (string | boolean)[][];
}

const headers: string[] = ['Studio 3T Enterprise', 'NoSQLBooster for MongoDB Commercial'];

const featureCategories: FeatureCategory[] = [
        { name: 'Basic', rows: [
          ['Operating System', 'Windows, Mac, Linux', 'Windows, Mac, Linux'],
          ['MongoDB Community Server', '3.0-6.0', '3.6-9.0'],
          ['MongoDB Enterprise Server', '3.0-6.0', '3.6-9.0'],
        ]},
        { name: 'Common Features', rows: [
          ['Embedded MongoDB Shell and ESNext Support', false, true],
          ['External MongoDB Shell Support', true, false],
          ['Fluent Query/Aggregate API', false, true],
          ['Operator Helper and PipelineBuilder', false, true],
          ['Shell Extensions (lodash, moment, shelljs, mathjs)', false, true],
          ['Using Node/NPM modules in your script', false, true],
          ['Quick Access to My Queries (My Queries Tab)', false, true],
          ['Learn MongoDB with Free Interactive Samples (Samples Tab)', false, true],
          ['Copy & Paste Database', true, true],
          ['Copy & Paste Collections', true, true],
          ['Aggregation Pipeline Wizard', true, false],
          ['Map-reduce Wizard', true, false],
          ['Visual Query Builder', true, true],
          ['Visual Explain Plan', true, true],
          ['Test Data Generator (Mock Data)', false, true],
          ['Schema Explorer', true, true],
          ['Schema Visualization', true, true],
          ['Schema Documentation', true, true],
          ['Data Comparison', true, false],
          ['Data Synchronization', true, false],
          ['Task Schedule', true, true],
          ['Data Masking', true, false],
          ['Re-schema Tool', true, true],
          ['Read-only Status-Lock', true, true],
          ['Auto Self-Update', true, true],
        ]},
        { name: 'Intelligent code editor', rows: [
          ['Code completion', 'partial', true],
          ['Field/Collection/Database name completion', 'partial', true],
          ['Code snippets', false, true],
          ['User-defined code snippets', false, true],
          ['Query script history', true, true],
          ['Syntax highlighting', true, true],
          ['Automatic indent and Code formatter', false, true],
          ['Code folding', false, true],
          ['On-the-fly error detection', false, true],
          ['Parameter Hints', true, true],
          ['Mouse Hover', false, true],
          ['Bracket and parentheses matching', true, true],
          ['Mark changed lines', false, true],
          ['Query Code (Translate MongoDB Query to Java, C#, Python, Nodejs)', true, true],
          ['Query Code (Translate MongoDB Query to PHP, Ruby)', true, false],
        ]},
        { name: 'Debugger capabilities', rows: [
          ['Breakpoints', false, true],
          ['Step-by-step Execution(Step over/into/out/stop)', false, true],
          ['Call Stack', false, true],
          ['Watch and Edit Variables', false, true],
          ['Debug Console REPL', false, true],
        ]},
        { name: 'Data view', rows: [
          ['Tree/Table/JSON View', true, true],
          ['In-Place Editing', true, true],
          ['CRUD Operations', true, true],
          ['Tooltip for array, object and ObjectId values', false, true],
          ['One-click Filter By (Query by Example)', false, true],
          ['One-click Group-By (COUNT, SUM, MIN, MAX, AVG)', false, true],
          ['One-click Order By (ASC, DESC)', false, true],
          ['One-click Project By', false, true],
          ['One-click Save Query as MongoDB Read-only View (Exclude/Include Visible Columns)', false, true],
          ['Binary Image Preview Tooltip', false, true],
          ['Visualize Geo JSON Data', false, true],
        ]},
        { name: 'Object Explorer/Management', rows: [
          ['Database Management', true, true],
          ['Collection Management', true, true],
          ['Index Management', true, true],
          ['GridFS Management', true, true],
          ['User and Role Management', true, true],
          ['Open MongoDB shell here', false, true],
          ['Show/hide system objects', false, true],
          ['Information-rich database object tooltip', true, true],
        ]},
        { name: 'Server Monitoring', rows: [
          ['In-progress Operations Viewer', false, true],
          ['Kill In-progress Operation', false, true],
          ['Database Profiler', false, true],
          ['Log Viewer: The Most Recent 1024 Mongod Events', false, true],
          ['Log Viewer: Parse External MongoDB Log File', false, true],
          ['Mongo Status Charts and Data Table (mongostat)', true, true],
          ['Mongo Top (Track Read And Write)', false, true],
          ['Troubleshoot Replica Sets', false, true],
          ['Server/Replica/Sharding Status', true, true],
        ]},
        { name: 'SQL Query', rows: [
          ['SQL Query (SELECT, WHERE, GROUP BY, HAVING, DISTINCT, ORDER BY, LIMIT)', true, true],
          ['SQL Functions (COUNT, SUM, MAX, MIN, AVG)', true, true],
          ['Date, String, Conversion Functions', false, true],
          ['Aggregation Pipeline Operators in SQL Query', false, true],
          ['SQL JOIN and Uncorrelated SubQuery', false, true],
          ['SQL Code Completion', false, true],
        ]},
        { name: 'Import and Export', rows: [
          ['One-click Export XLSX and View as Spreadsheet in Excel...', false, true],
          ['Export to JSON', true, true],
          ['Export to CSV', true, true],
          ['Export to SQL', true, true],
          ['mongodump / mongorestore integration', true, true],
          ['mongoimport / mongoexport integration', true, true],
          ['Import from CSV', true, true],
          ['Import from JSON', true, true],
          ['Import from MySql, PostgreSql and MSSQL', true, true],
          ['Import from Oracle', true, false],
          ['Import from DB2', true, false],
        ]},
        { name: 'Command Line Interface', uri: '/nbcli', rows: [
          ['Run JavaScript Script File', false, true],
          ['Run JavaScript Statements', false, true],
          ['Run SQL Query Statements', false, true],
          ['Run Tasks', false, true],
        ]},
        { name: 'Connectivity', rows: [
          ['Direct Connection', true, true],
          ['Replica Set', true, true],
          ['Sharded Cluster', true, true],
          ['SSH Tunnel Integration', true, true],
          ['SSL Support', true, true],
        ]},
        { name: 'Authentication', rows: [
          ['SCRAM-SHA-1 Authentication', true, true],
          ['SCRAM-SHA-256 Authentication', true, true],
          ['MONGODB-CR Authentication', false, true],
          ['X.509 authentication', true, true],
          ['Kerberos (GSSAPI) authentication', true, true],
          ['LDAP (PLAIN) authentication', true, true],
          ['MONGODB-AWS authentication', true, true],
          ['MONGODB-OIDC Authentication', true, true],
        ]},
      ];
</script>
